import {useQueryClient} from '@tanstack/react-query';
import {useCallback, useState} from 'react';
import {Navigate, Outlet, useNavigate} from 'react-router-dom';

import {PANEL_SNAPSHOT_QUERY_KEY, usePanel} from '../app/PanelProvider.js';
import {
  limparRegistroDeSessao,
  useSessionKeeper,
} from '../auth/session-keeper.js';
import {useSessionProbe} from '../auth/session-probe.js';
import {isPanelApiError} from '../data/PanelDataSource.js';
import {Brand} from './Brand.js';
import {EmergencyPauseButton} from './EmergencyPauseButton.js';
import {EnvironmentStatus} from './EnvironmentStatus.js';
import {PrimaryNav} from './PrimaryNav.js';
import {ReauthDialog} from './ReauthDialog.js';
import {ToastRegion} from './ToastRegion.js';

/** Id do fragmento aprovado. Os testes visuais capturam este elemento. */
export const PANEL_ROOT_ID = 'devmil-instagram-preview';

/**
 * A moldura do painel, com a porta de autenticação do caminho HTTP.
 *
 * Tudo que é de autenticação aqui fica atrás de `dataSource.auth`: no modo
 * fixture a porta é `null`, nada disso renderiza nem agenda, e o DOM que a
 * suíte visual captura é byte a byte o de antes desta etapa.
 */
export const AppShell = () => {
  const {snapshot, snapshotError, paused, dataSource} = usePanel();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = dataSource.auth;

  /** A sessão morreu sozinha e o painel ainda está na tela, com o aviso. */
  const [sessaoVencida, setSessaoVencida] = useState(false);

  const irParaEntrada = useCallback(() => {
    // O snapshot em cache é de uma sessão que acabou. `resetQueries`, e não
    // `removeQueries`: o observer continua montado no provider, e remover a
    // query debaixo dele a deixa parada sem refetch quando a pessoa voltar.
    void queryClient.resetQueries({queryKey: PANEL_SNAPSHOT_QUERY_KEY});
    void navigate('/entrar', {replace: true});
  }, [navigate, queryClient]);

  /**
   * Sessão perdida pelo guardião: AVISA, e não navega.
   *
   * Navegar aqui desmonta o `Outlet` inteiro, e com ele todo formulário aberto.
   * A janela de ociosidade é de 30 minutos e o limite absoluto de 8 horas, então
   * o caso comum é o dono voltar de um intervalo com um comentário de rejeição
   * ou uma resolução de privacidade digitada pela metade: o texto sumiria sem
   * aviso e sem chance de copiar. O aviso é deliberadamente NÃO modal, porque
   * um backdrop por cima impediria justamente o copiar. Sair da tela vira ato
   * do dono, no botão.
   *
   * A sessão já está morta de qualquer jeito: o que sobrou na tela é o snapshot
   * em cache, e toda escrita a partir daqui volta 401. Por isso o aviso diz que
   * nada mais é enviado, em vez de fingir que o painel continua útil.
   */
  const aoPerderSessao = useCallback(() => {
    setSessaoVencida(true);
  }, []);

  // O guardião só trabalha com painel autenticado de fato: antes do primeiro
  // snapshot não há certeza de sessão, e renovar às cegas brigaria com a
  // própria tela de entrada.
  useSessionKeeper({
    auth: snapshot && auth ? auth : null,
    onSessionLost: aoPerderSessao,
  });

  /**
   * A sonda, no MESMO caminho do guardião.
   *
   * O guardião só acorda perto do vencimento por ociosidade, e até lá trocar de
   * aba mostrava o snapshot em cache de uma sessão já morta. A sonda cobre a
   * troca de rota e o retorno de foco; o 401 dela chama o mesmo
   * `aoPerderSessao`, então o desfecho continua sendo o aviso, e não a
   * navegação forçada que este componente decidiu não fazer.
   *
   * Com o aviso já na tela a porta é desligada de propósito: a sessão está
   * morta, o painel já disse isso, e continuar sondando a cada troca de aba só
   * gastaria requisição para reaprender a mesma coisa.
   */
  useSessionProbe({
    auth: snapshot && auth && !sessaoVencida ? auth : null,
    onSessionLost: aoPerderSessao,
  });

  const sair = useCallback(() => {
    if (!auth) {
      return;
    }
    void (async () => {
      try {
        await auth.logout();
      } catch {
        // Sessão já morta ou rede fora: nos dois casos o lugar certo para o
        // usuário é a tela de entrada, não um painel preso a uma sessão que
        // ele mandou encerrar.
      }
      limparRegistroDeSessao(globalThis.localStorage);
      // Sair é ato explícito: aqui a navegação é imediata mesmo, porque foi o
      // dono que pediu. O aviso acima existe para a sessão que morre sozinha.
      irParaEntrada();
    })();
  }, [auth, irParaEntrada]);

  if (!snapshot) {
    // 401 no snapshot com porta de autenticação é "não está logado", e a
    // resposta é a tela de entrada, nunca um "Carregando…" eterno. Qualquer
    // outro erro mantém o comportamento anterior desta moldura.
    if (
      auth &&
      isPanelApiError(snapshotError) &&
      snapshotError.status === 401
    ) {
      return <Navigate to="/entrar" replace />;
    }
    return (
      <div id={PANEL_ROOT_ID}>
        <p className="dm-loading" role="status">
          Carregando o painel…
        </p>
      </div>
    );
  }

  return (
    <div id={PANEL_ROOT_ID}>
      <div className="dm-app">
        <aside className="dm-sidebar" aria-label="Navegação principal">
          <Brand />
          <PrimaryNav />
          <div className="dm-side-status">
            <small>Autonomia configurada</small>
            <strong>{`Nível ${snapshot.operation.configuredAutonomyLevel} · supervisionado`}</strong>
          </div>
          {auth ? (
            <button type="button" className="dm-logout" onClick={sair}>
              Sair do painel
            </button>
          ) : null}
        </aside>

        <main className="dm-main">
          <header className="dm-topbar">
            <EnvironmentStatus operation={snapshot.operation} />
            <EmergencyPauseButton />
          </header>

          <div className="dm-content">
            {sessaoVencida ? (
              <div className="dm-alert" role="alert">
                <p>
                  Sua sessão venceu e nada mais é enviado a partir desta tela.
                  Copie o que estiver escrito antes de entrar de novo.
                </p>
                <button
                  type="button"
                  className="dm-action"
                  onClick={irParaEntrada}
                >
                  Entrar de novo
                </button>
              </div>
            ) : null}
            {paused ? (
              <div className="dm-alert" role="alert">
                Operação pausada. Publicações e respostas automáticas estão
                bloqueadas.
              </div>
            ) : null}
            <Outlet />
            <ToastRegion />
          </div>
        </main>
      </div>
      <ReauthDialog />
    </div>
  );
};
